import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import '../styles/Messenger.css';

// ─── Types ────────────────────────────────────────────────────

interface Contact {
  id: string;
  type: 'dm' | 'group';
  name: string;
  email?: string;
  avatar_letter: string;
  avatar_color?: string;
  members?: GroupMember[];
  is_pinned: boolean;
  last_message?: string;
  last_message_time?: string;
  unread_count: number;
}

interface GroupMember {
  user_id: string;
  full_name: string;
  email: string;
  avatar_letter: string;
}

interface RpcOrgMember {
  user_id: string;
  full_name?: string;
  email?: string;
}

interface Message {
  id: string;
  sender_id: string;
  receiver_id?: string;
  group_id?: string;
  content: string;
  message_type: 'text' | 'image' | 'file';
  file_name?: string;
  file_size?: number;
  created_at: string;
  updated_at?: string;
  read_at: string | null;
  is_deleted?: boolean;
}

interface MessengerProps { onClose: () => void; onUnreadChange?: (n: number) => void; }

// ─── Constants ────────────────────────────────────────────────

const AVATAR_COLORS = [
  'linear-gradient(135deg,#f59e0b,#ef4444)',
  'linear-gradient(135deg,#6366f1,#8b5cf6)',
  'linear-gradient(135deg,#10b981,#059669)',
  'linear-gradient(135deg,#3b82f6,#2563eb)',
  'linear-gradient(135deg,#ec4899,#db2777)',
  'linear-gradient(135deg,#f97316,#ea580c)',
];

const EMOJI_LIST = [
  '😀','😂','😍','🥰','😎','🤔','😅','🙌','👍','❤️',
  '🔥','✅','🎉','😢','😡','👏','🤝','💪','🙏','😊',
  '😆','🥳','😴','🤗','😇','🤩','😬','😳','🤯','😱',
  '👋','✌️','🫡','💯','⭐','🚀','💡','📌','📎','🗂️',
];

const avatarColor  = (s: string) => AVATAR_COLORS[s.charCodeAt(0) % AVATAR_COLORS.length];
const formatTime   = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
const formatSize   = (b: number)   => b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

// ─── Component ────────────────────────────────────────────────

const Messenger: React.FC<MessengerProps> = ({ onClose, onUnreadChange }) => {
  const { user, currentOrg } = useAuth();

  // contacts
  const [allContacts, setAllContacts]         = useState<Contact[]>([]);
  const [orgMembers, setOrgMembers]           = useState<GroupMember[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [activeTab, setActiveTab]             = useState<'all'|'personal'|'groups'>('all');
  const [search, setSearch]                   = useState('');
  const [totalUnread, setTotalUnreadState]    = useState(0);
  // Stable ref so setTotalUnread never changes identity and doesn't re-trigger loadContacts
  const onUnreadChangeRef = useRef(onUnreadChange);
  useEffect(() => { onUnreadChangeRef.current = onUnreadChange; }, [onUnreadChange]);
  const setTotalUnread = useCallback((n: number | ((prev: number) => number)) => {
    setTotalUnreadState(prev => {
      const next = typeof n === 'function' ? n(prev) : n;
      onUnreadChangeRef.current?.(next);
      return next;
    });
  }, []); // no deps — stable for lifetime of component

  // chat
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [selected, setSelected]     = useState<Contact | null>(null);
  const [messages, setMessages]     = useState<Message[]>([]);
  const [newMsg, setNewMsg]         = useState('');
  const [sending, setSending]       = useState(false);
  const [uploading, setUploading]   = useState(false);

  // emoji picker
  const [showEmoji, setShowEmoji]   = useState(false);

  // voice
  const [recording, setRecording]   = useState(false);
  const recognitionRef              = useRef<SpeechRecognition | null>(null);

  // edit / delete
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [editText, setEditText]     = useState('');
  const [menuMsgId, setMenuMsgId]   = useState<string | null>(null);

  // group creation
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName]             = useState('');
  const [groupSelected, setGroupSelected]     = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const globalChannelRef = useRef<RealtimeChannel | null>(null);  // listens to ALL incoming DMs
  const chatChannelRef   = useRef<RealtimeChannel | null>(null);  // listens to active chat
  const selectedRef      = useRef<Contact | null>(null);  // stable ref — avoids re-subscription on tab switch
  const fileInputRef     = useRef<HTMLInputElement>(null);
  const textareaRef      = useRef<HTMLTextAreaElement>(null);
  const emojiRef         = useRef<HTMLDivElement>(null);

  // keep selectedRef in sync without triggering realtime re-subscription
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // ── Load contacts (once on mount) ─────────────────────────────
  const hasLoadedOnce = useRef(false);
  const loadContacts = useCallback(async () => {
    if (!currentOrg || !user) return;
    // Only show spinner on first load — subsequent calls must not blink/reset the list
    if (!hasLoadedOnce.current) setLoadingContacts(true);
    try {
      const { data, error } = await supabase
        .rpc('get_org_members_with_emails', { p_org_id: currentOrg.id });
      if (error) throw error;

      const members: GroupMember[] = (data as RpcOrgMember[] || [])
        .filter((m: RpcOrgMember) => m.user_id !== user.id)
        .map((m: RpcOrgMember) => ({
          user_id:      m.user_id,
          full_name:    m.full_name || m.email || 'Unknown',
          email:        m.email || '',
          avatar_letter:(m.full_name || m.email || '?').charAt(0).toUpperCase(),
        }));
      setOrgMembers(members);

      const pinned: string[]   = JSON.parse(localStorage.getItem(`msng_pinned_${user.id}`) || '[]');
      const groups: Contact[]  = JSON.parse(localStorage.getItem(`msng_groups_${user.id}_${currentOrg.id}`) || '[]');

      // Fetch last message for every DM contact in one query
      const memberIds = members.map(m => m.user_id);
      let lastMsgMap: Record<string, { text: string; time: string; ts: number }> = {};

      if (memberIds.length > 0) {
        // Get the most recent message per DM pair
        const { data: lastMsgs } = await supabase
          .from('direct_messages')
          .select('sender_id, receiver_id, content, message_type, created_at')
          .or(memberIds.map(id =>
            `and(sender_id.eq.${user.id},receiver_id.eq.${id}),and(sender_id.eq.${id},receiver_id.eq.${user.id})`
          ).join(','))
          .order('created_at', { ascending: false });

        // Keep only the latest per contact
        (lastMsgs as Pick<Message, 'sender_id' | 'receiver_id' | 'content' | 'message_type' | 'created_at'>[] || []).forEach((row) => {
          const contactId = row.sender_id === user.id ? row.receiver_id : row.sender_id;
          if (!contactId || lastMsgMap[contactId]) return;
          {
            const preview = row.message_type === 'image' ? '📷 Image'
              : row.message_type === 'file' ? '📎 File'
              : row.content;
            lastMsgMap[contactId] = {
              text: preview,
              time: formatTime(row.created_at),
              ts:   new Date(row.created_at).getTime(),
            };
          }
        });

        // Also fetch last message for groups
        const groupIds = groups.map(g => g.id);
        if (groupIds.length > 0) {
          const { data: grpMsgs } = await supabase
            .from('direct_messages')
            .select('group_id, content, message_type, created_at')
            .in('group_id', groupIds)
            .order('created_at', { ascending: false });

          (grpMsgs || []).forEach((row: any) => {
            if (!lastMsgMap[row.group_id]) {
              const preview = row.message_type === 'image' ? '📷 Image'
                : row.message_type === 'file' ? '📎 File'
                : row.content;
              lastMsgMap[row.group_id] = {
                text: preview,
                time: formatTime(row.created_at),
                ts:   new Date(row.created_at).getTime(),
              };
            }
          });
        }
      }

      const dmContacts: Contact[] = members.map(m => ({
        id: m.user_id, type: 'dm' as const, name: m.full_name, email: m.email,
        avatar_letter: m.avatar_letter, avatar_color: avatarColor(m.user_id),
        is_pinned: pinned.includes(m.user_id),
        last_message:      lastMsgMap[m.user_id]?.text || '',
        last_message_time: lastMsgMap[m.user_id]?.time || '',
        last_message_ts:   lastMsgMap[m.user_id]?.ts   || 0,
        unread_count: 0,
      }));

      const allC = [
        ...dmContacts,
        ...groups.map(g => ({
          ...g,
          is_pinned:         pinned.includes(g.id),
          avatar_color:      avatarColor(g.id),
          last_message:      lastMsgMap[g.id]?.text || g.last_message || '',
          last_message_time: lastMsgMap[g.id]?.time || g.last_message_time || '',
          last_message_ts:   lastMsgMap[g.id]?.ts   || 0,
        })),
      ];

      // Sort by most recent message first
      allC.sort((a, b) => ((b as any).last_message_ts || 0) - ((a as any).last_message_ts || 0));

      setAllContacts(allC);

      // Auto-open the most recent conversation (first load only)
      if (!hasLoadedOnce.current) {
        const mostRecent = allC.find(c => (c as any).last_message_ts > 0);
        if (mostRecent && !selectedRef.current) setSelected(mostRecent);
        hasLoadedOnce.current = true;
      }
    } catch (e) {
      console.error('Failed to load contacts', e);
    } finally {
      setLoadingContacts(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, user?.id]); // primitives only — stable reference

  useEffect(() => { loadContacts(); }, [loadContacts]);

  // ── Global realtime channel (ALL incoming DMs) ────────────────
  // One channel, set up once. Uses selectedRef so it never needs re-subscribing.
  useEffect(() => {
    if (!user) return;
    globalChannelRef.current?.unsubscribe();
    globalChannelRef.current = supabase
      .channel(`inbox:${user.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'direct_messages',
      }, (payload) => {
        const msg  = payload.new as Message;
        const cur  = selectedRef.current;
        const isForActiveChat =
          cur && (
            (cur.type === 'dm' && (
              (msg.sender_id === cur.id && msg.receiver_id === user.id) ||
              (msg.sender_id === user.id && msg.receiver_id === cur.id)
            )) ||
            (cur.type === 'group' && msg.group_id === cur.id)
          );

        if (payload.eventType === 'INSERT') {
          if (isForActiveChat) {
            // Only add if not already present (we add optimistically on send)
            setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
          } else if (msg.sender_id !== user.id) {
            // Increment unread for the right contact
            const contactId = msg.group_id || msg.sender_id;
            setAllContacts(prev => prev.map(c =>
              c.id === contactId ? { ...c, unread_count: c.unread_count + 1 } : c
            ));
            setTotalUnread(n => n + 1);
          }
        }

        if (payload.eventType === 'UPDATE' && isForActiveChat) {
          setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, ...msg } : m));
        }

        if (payload.eventType === 'DELETE') {
          const deleted = payload.old as Message;
          setMessages(prev => prev.filter(m => m.id !== deleted.id));
        }
      })
      .subscribe();

    return () => { globalChannelRef.current?.unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // only user id — stable

  // ── Load messages when selected contact changes ────────────────
  useEffect(() => {
    if (!selected || !user) { setMessages([]); return; }

    // Clear unread for this contact
    setAllContacts(prev => prev.map(c =>
      c.id === selected.id ? { ...c, unread_count: 0 } : c
    ));
    setTotalUnread(prev => Math.max(0, prev - (selected.unread_count || 0)));

    const load = async () => {
      let data: any[] = [];
      if (selected.type === 'dm') {
        const res = await supabase
          .from('direct_messages').select('*')
          .or(`and(sender_id.eq.${user.id},receiver_id.eq.${selected.id}),and(sender_id.eq.${selected.id},receiver_id.eq.${user.id})`)
          .order('created_at', { ascending: true });
        data = res.data || [];
      } else {
        const res = await supabase
          .from('direct_messages').select('*')
          .eq('group_id', selected.id)
          .order('created_at', { ascending: true });
        data = res.data || [];
      }
      setMessages(data as Message[]);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, user?.id]); // primitives — don't re-fire on object identity changes

  // ── Auto scroll ────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]); // length only — avoids scroll on edit/delete

  // ── Close emoji picker on outside click ───────────────────────
  useEffect(() => {
    if (!showEmoji) return;
    const handler = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setShowEmoji(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmoji]);

  // ── Close msg context menu on outside click ───────────────────
  useEffect(() => {
    if (!menuMsgId) return;
    const handler = () => setMenuMsgId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuMsgId]);

  // ── Send text ─────────────────────────────────────────────────
  const resetTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !selected || !user || sending) return;
    const content = newMsg.trim();
    setNewMsg('');
    resetTextarea();
    setShowEmoji(false);
    setSending(true);
    try {
      const payload: any = { sender_id: user.id, content, message_type: 'text' };
      if (selected.type === 'dm') payload.receiver_id = selected.id;
      else payload.group_id = selected.id;
      const { data, error } = await supabase.from('direct_messages').insert(payload).select().single();
      if (error) throw error;
      setMessages(prev => [...prev, data as Message]);
    } catch (e) {
      console.error('Send failed', e); setNewMsg(content);
    } finally { setSending(false); }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    if (e.key === 'Escape') { setEditingId(null); setShowEmoji(false); }
  };

  // ── Edit message ──────────────────────────────────────────────
  const startEdit = (msg: Message) => {
    setEditingId(msg.id);
    setEditText(msg.content);
    setMenuMsgId(null);
  };

  const saveEdit = async () => {
    if (!editingId || !editText.trim()) return;
    const { error } = await supabase
      .from('direct_messages')
      .update({ content: editText.trim(), updated_at: new Date().toISOString() })
      .eq('id', editingId);
    if (!error) {
      setMessages(prev => prev.map(m =>
        m.id === editingId ? { ...m, content: editText.trim() } : m
      ));
    }
    setEditingId(null);
  };

  // ── Delete message ────────────────────────────────────────────
  const deleteMsg = async (msgId: string) => {
    setMenuMsgId(null);
    if (!window.confirm('Delete this message?')) return;
    const { error } = await supabase.from('direct_messages').delete().eq('id', msgId);
    if (!error) setMessages(prev => prev.filter(m => m.id !== msgId));
  };

  // ── Attachment ────────────────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selected || !user) return;
    e.target.value = '';
    setUploading(true);
    try {
      const ext  = file.name.split('.').pop();
      const path = `messages/${user.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('attachments').upload(path, file);
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('attachments').getPublicUrl(path);
      const isImage = file.type.startsWith('image/');
      const payload: any = {
        sender_id: user.id, content: urlData.publicUrl,
        message_type: isImage ? 'image' : 'file', file_name: file.name, file_size: file.size,
      };
      if (selected.type === 'dm') payload.receiver_id = selected.id;
      else payload.group_id = selected.id;
      const { data, error } = await supabase.from('direct_messages').insert(payload).select().single();
      if (error) throw error;
      setMessages(prev => [...prev, data as Message]);
    } catch (err) { console.error('Upload failed', err); }
    finally { setUploading(false); }
  };

  // ── Voice recognition ─────────────────────────────────────────
  const toggleVoice = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert('Speech recognition not supported in this browser.'); return; }
    if (recording) {
      recognitionRef.current?.stop();
      setRecording(false);
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.onresult = (e: any) => {
      const transcript = Array.from(e.results).map((r: any) => r[0].transcript).join(' ');
      setNewMsg(prev => (prev ? prev + ' ' : '') + transcript);
    };
    rec.onerror = () => setRecording(false);
    rec.onend   = () => setRecording(false);
    rec.start();
    recognitionRef.current = rec;
    setRecording(true);
  };

  // ── Pin / unpin ────────────────────────────────────────────────
  const togglePin = (contact: Contact, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    const key = `msng_pinned_${user.id}`;
    const pins: string[] = JSON.parse(localStorage.getItem(key) || '[]');
    const next = pins.includes(contact.id) ? pins.filter(p => p !== contact.id) : [...pins, contact.id];
    localStorage.setItem(key, JSON.stringify(next));
    setAllContacts(prev => prev.map(c => c.id === contact.id ? { ...c, is_pinned: !c.is_pinned } : c));
    if (selected?.id === contact.id) setSelected(s => s ? { ...s, is_pinned: !s.is_pinned } : s);
  };

  // ── Delete group ───────────────────────────────────────────────
  const deleteGroup = (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || !currentOrg || !window.confirm('Delete this group?')) return;
    const key = `msng_groups_${user.id}_${currentOrg.id}`;
    const groups: Contact[] = JSON.parse(localStorage.getItem(key) || '[]');
    localStorage.setItem(key, JSON.stringify(groups.filter(g => g.id !== groupId)));
    setAllContacts(prev => prev.filter(c => c.id !== groupId));
    if (selected?.id === groupId) setSelected(null);
  };

  // ── Create group ───────────────────────────────────────────────
  const handleCreateGroup = () => {
    if (!groupName.trim() || groupSelected.length < 1 || !user || !currentOrg) return;
    const members = orgMembers.filter(m => groupSelected.includes(m.user_id));
    const group: Contact = {
      id: `grp_${Date.now()}`, type: 'group', name: groupName.trim(),
      avatar_letter: groupName.charAt(0).toUpperCase(), avatar_color: avatarColor(groupName),
      members, is_pinned: false, last_message: '', last_message_time: '', unread_count: 0,
    };
    const key = `msng_groups_${user.id}_${currentOrg.id}`;
    const existing: Contact[] = JSON.parse(localStorage.getItem(key) || '[]');
    localStorage.setItem(key, JSON.stringify([...existing, group]));
    setAllContacts(prev => [...prev, group]);
    setGroupName(''); setGroupSelected([]); setShowCreateGroup(false);
    setActiveTab('groups'); setSelected(group);
  };

  // ── Helpers ────────────────────────────────────────────────────
  const getSenderName = (msg: Message) => {
    if (msg.sender_id === user?.id) return 'You';
    if (selected?.type === 'group') return selected.members?.find(x => x.user_id === msg.sender_id)?.full_name || 'Member';
    return selected?.name || '';
  };
  const getSenderLetter = (msg: Message) => {
    if (msg.sender_id === user?.id) return (user?.user_metadata?.full_name || user?.email || 'Y').charAt(0).toUpperCase();
    if (selected?.type === 'group') return (selected.members?.find(x => x.user_id === msg.sender_id)?.full_name || '?').charAt(0).toUpperCase();
    return selected?.avatar_letter || '?';
  };

  const grouped = messages.reduce<{ date: string; msgs: Message[] }[]>((acc, msg) => {
    const date = new Date(msg.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const last = acc[acc.length - 1];
    if (last?.date === date) last.msgs.push(msg); else acc.push({ date, msgs: [msg] });
    return acc;
  }, []);

  const filtered  = allContacts.filter(c => {
    const matchTab = activeTab === 'all' ? true : activeTab === 'personal' ? c.type === 'dm' : c.type === 'group';
    const q = search.toLowerCase();
    return matchTab && (!q || c.name.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q));
  });
  const pinnedList   = filtered.filter(c => c.is_pinned);
  const messagesList = filtered.filter(c => !c.is_pinned);

  // ── Contact row ────────────────────────────────────────────────
  const ContactRow = ({ c }: { c: Contact }) => (
    <div className={`msng-contact ${selected?.id === c.id ? 'active' : ''}`} onClick={() => { setSelected(c); setMobileChatOpen(true); }}>
      <div className="msng-contact-avatar" style={{ background: c.avatar_color }}>
        {c.avatar_letter}
        {c.type === 'group' && <span className="msng-group-dot">G</span>}
      </div>
      <div className="msng-contact-info">
        <div className="msng-contact-top">
          <span className="msng-contact-name">{c.name}</span>
          <div className="msng-contact-top-right">
            {c.last_message_time && <span className="msng-contact-time">{c.last_message_time}</span>}
            <div className="msng-contact-actions">
              <button className={`msng-pin-btn ${c.is_pinned ? 'pinned' : ''}`} onClick={e => togglePin(c, e)} title={c.is_pinned ? 'Unpin' : 'Pin'}>📌</button>
              {c.type === 'group' && <button className="msng-del-group-btn" onClick={e => deleteGroup(c.id, e)} title="Delete group">🗑</button>}
            </div>
          </div>
        </div>
        <div className="msng-contact-bottom">
          <span className="msng-contact-preview">{c.last_message || (c.type === 'group' ? `${c.members?.length || 0} members` : 'No messages yet')}</span>
          {c.unread_count > 0 && <span className="msng-unread-badge">{c.unread_count}</span>}
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <div className="msng-overlay" onClick={onClose}>
      <div className={`msng-popup ${mobileChatOpen ? 'msng-mobile-chat-open' : ''}`} onClick={e => e.stopPropagation()}>

        {/* ── LEFT SIDEBAR ──────────────────────────────── */}
        <div className="msng-sidebar">
          <div className="msng-search-wrap">
            <svg className="msng-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input className="msng-search" placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="msng-tabs">
            {(['all','personal','groups'] as const).map(t => (
              <button key={t} className={`msng-tab ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          <div className="msng-contacts">
            {loadingContacts ? <div className="msng-loading">Loading…</div> : (
              <>
                {pinnedList.length > 0 && (
                  <>
                    <div className="msng-section-label"><span>Pinned Message</span></div>
                    {pinnedList.map(c => <ContactRow key={c.id} c={c} />)}
                  </>
                )}
                <div className="msng-section-label">
                  <span>Messages</span>
                  {(activeTab === 'groups' || activeTab === 'all') && (
                    <button className="msng-new-group-btn" onClick={() => setShowCreateGroup(true)} title="New Group">+</button>
                  )}
                </div>
                {messagesList.length === 0 && (
                  <div className="msng-empty-contacts">
                    {activeTab === 'groups' ? 'No groups yet — create one!' : 'No conversations yet'}
                  </div>
                )}
                {messagesList.map(c => <ContactRow key={c.id} c={c} />)}
              </>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL ───────────────────────────────── */}
        <div className="msng-chat">
          {showCreateGroup ? (
            <div className="msng-create-group">
              <div className="msng-chat-header">
                <span className="msng-chat-name">New Group</span>
                <button className="msng-close-btn" onClick={() => setShowCreateGroup(false)}>✕</button>
              </div>
              <div className="msng-create-group-body">
                <input className="msng-group-name-input" placeholder="Group name" value={groupName} onChange={e => setGroupName(e.target.value)} />
                <p className="msng-group-members-label">Select members</p>
                <div className="msng-group-member-list">
                  {orgMembers.map(m => (
                    <label key={m.user_id} className="msng-group-member-row">
                      <div className="msng-contact-avatar msng-member-avatar" style={{ background: avatarColor(m.user_id) }}>{m.avatar_letter}</div>
                      <div className="msng-group-member-info">
                        <span className="msng-contact-name">{m.full_name}</span>
                        <span className="msng-contact-preview">{m.email}</span>
                      </div>
                      <input type="checkbox" className="msng-member-check" checked={groupSelected.includes(m.user_id)}
                        onChange={e => setGroupSelected(prev => e.target.checked ? [...prev, m.user_id] : prev.filter(id => id !== m.user_id))} />
                    </label>
                  ))}
                </div>
                {groupSelected.length > 0 && <div className="msng-group-selected-preview">{groupSelected.length} member{groupSelected.length > 1 ? 's' : ''} selected</div>}
                <button className="msng-create-group-btn" disabled={!groupName.trim() || groupSelected.length < 1} onClick={handleCreateGroup}>Create Group</button>
              </div>
            </div>
          ) : selected ? (
            <>
              {/* Header */}
              <div className="msng-chat-header">
                <div className="msng-chat-header-left">
                  {/* Back button — mobile only */}
                  <button className="msng-back-btn" onClick={() => setMobileChatOpen(false)} title="Back">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <div className="msng-chat-avatar" style={{ background: selected.avatar_color }}>{selected.avatar_letter}</div>
                  <div>
                    <div className="msng-chat-name">{selected.name}</div>
                    <div className="msng-chat-status">
                      {selected.type === 'group'
                        ? <span className="msng-offline">{selected.members?.length || 0} members</span>
                        : <span className="msng-offline">Direct message</span>}
                    </div>
                  </div>
                </div>
                <button className="msng-close-btn" onClick={onClose}>✕</button>
              </div>

              {/* Messages */}
              <div className="msng-messages" onClick={() => setMenuMsgId(null)}>
                {grouped.map(({ date, msgs }) => (
                  <React.Fragment key={date}>
                    <div className="msng-date-divider"><span>{date}</span></div>
                    {msgs.map(msg => {
                      const isMine = msg.sender_id === user?.id;
                      const isEditing = editingId === msg.id;
                      return (
                        <div key={msg.id} className={`msng-msg-row ${isMine ? 'mine' : 'theirs'}`}>
                          {!isMine && (
                            <div className="msng-msg-avatar" style={{ background: avatarColor(msg.sender_id) }}>
                              {getSenderLetter(msg)}
                            </div>
                          )}
                          <div className="msng-msg-body">
                            <div className="msng-msg-meta">
                              <span className="msng-msg-sender">{getSenderName(msg)}</span>
                              <span className="msng-msg-time">
                                {formatTime(msg.created_at)}
                                {msg.updated_at && msg.updated_at !== msg.created_at && <span className="msng-edited"> · edited</span>}
                              </span>
                            </div>
                            {isEditing ? (
                              <div className="msng-edit-wrap">
                                <input
                                  className="msng-edit-input"
                                  value={editText}
                                  onChange={e => setEditText(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                                  autoFocus
                                />
                                <div className="msng-edit-actions">
                                  <button className="msng-edit-save" onClick={saveEdit}>Save</button>
                                  <button className="msng-edit-cancel" onClick={() => setEditingId(null)}>Cancel</button>
                                </div>
                              </div>
                            ) : msg.message_type === 'image' ? (
                              <div className={`msng-bubble ${isMine ? 'bubble-mine' : 'bubble-theirs'} bubble-img`}>
                                <img src={msg.content} alt={msg.file_name || 'image'} className="msng-img-preview" onClick={() => window.open(msg.content, '_blank')} />
                              </div>
                            ) : msg.message_type === 'file' ? (
                              <div className={`msng-bubble ${isMine ? 'bubble-mine' : 'bubble-theirs'} bubble-file`}>
                                <a href={msg.content} target="_blank" rel="noreferrer" className="msng-file-link">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                  </svg>
                                  <div className="msng-file-info">
                                    <span className="msng-file-name">{msg.file_name || 'File'}</span>
                                    {msg.file_size && <span className="msng-file-size">{formatSize(msg.file_size)}</span>}
                                  </div>
                                </a>
                              </div>
                            ) : (
                              <div className={`msng-bubble ${isMine ? 'bubble-mine' : 'bubble-theirs'}`}>{msg.content}</div>
                            )}
                          </div>

                          {/* Actions menu (own messages only) */}
                          {isMine && !isEditing && msg.message_type === 'text' && (
                            <div className="msng-msg-menu-wrap">
                              <button className="msng-msg-more" onClick={e => { e.stopPropagation(); setMenuMsgId(menuMsgId === msg.id ? null : msg.id); }}>⋮</button>
                              {menuMsgId === msg.id && (
                                <div className="msng-msg-menu">
                                  <button onClick={() => startEdit(msg)}>✏️ Edit</button>
                                  <button onClick={() => deleteMsg(msg.id)}>🗑 Delete</button>
                                </div>
                              )}
                            </div>
                          )}
                          {isMine && !isEditing && msg.message_type !== 'text' && (
                            <div className="msng-msg-menu-wrap">
                              <button className="msng-msg-more" onClick={e => { e.stopPropagation(); setMenuMsgId(menuMsgId === msg.id ? null : msg.id); }}>⋮</button>
                              {menuMsgId === msg.id && (
                                <div className="msng-msg-menu">
                                  <button onClick={() => deleteMsg(msg.id)}>🗑 Delete</button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                {uploading && (
                  <div className="msng-msg-row mine">
                    <div className="msng-msg-body">
                      <div className="msng-bubble bubble-mine msng-uploading">Uploading…</div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Emoji picker */}
              {showEmoji && (
                <div className="msng-emoji-picker" ref={emojiRef}>
                  {EMOJI_LIST.map(e => (
                    <button key={e} className="msng-emoji-item" onClick={() => setNewMsg(prev => prev + e)}>
                      {e}
                    </button>
                  ))}
                </div>
              )}

              {/* Input bar */}
              <div className="msng-input-row">
                <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip" style={{ display: 'none' }} onChange={handleFileChange} />
                <button className="msng-attach-btn" title="Attach" onClick={() => fileInputRef.current?.click()}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <textarea
                  ref={textareaRef}
                  className="msng-input"
                  placeholder={recording ? 'Listening…' : 'Type a message…'}
                  value={newMsg}
                  rows={1}
                  onChange={e => {
                    setNewMsg(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={sending || uploading}
                />
                <button className={`msng-emoji-btn ${showEmoji ? 'active' : ''}`} title="Emoji" onClick={() => setShowEmoji(v => !v)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/>
                    <line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
                  </svg>
                </button>
                <button className={`msng-mic-btn ${recording ? 'recording' : ''}`} title={recording ? 'Stop recording' : 'Voice input'} onClick={toggleVoice}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </button>
                <button
                  className="msng-send-btn"
                  title="Send"
                  onClick={handleSend}
                  disabled={!newMsg.trim() || sending || uploading}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="msng-no-chat">
              <div className="msng-no-chat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <p>Select a contact to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { Messenger as default };
export type { MessengerProps };
export const messengerTotalUnread = 0; // re-exported for FAB badge — see Layout
